/**
 * useOffline Hook
 *
 * React hook for managing offline state and connectivity detection.
 * Provides reactive state for online/offline status and pending mutation count.
 *
 * Requirements: 6.1, 6.2, 6.3, 6.5
 */

import { useState, useEffect, useCallback } from 'react';
import { offlineManager, type SyncConflict } from '../lib/offline';

export interface UseOfflineResult {
  /** Whether the device is currently online */
  isOnline: boolean;
  /** Number of mutations waiting to be synced */
  pendingMutationCount: number;
  /** Whether a sync is currently in progress */
  isSyncing: boolean;
  /** Whether we just came back online (for showing "Back Online" message) */
  justReconnected: boolean;
  /** Clear the justReconnected flag */
  clearReconnectedFlag: () => void;
  /** Last sync conflict that occurred */
  lastConflict: SyncConflict | null;
  /** Clear the last conflict */
  clearConflict: () => void;
}

/**
 * Hook for tracking offline state and connectivity
 *
 * Usage:
 * ```tsx
 * const { isOnline, pendingMutationCount, justReconnected } = useOffline();
 *
 * if (!isOnline) {
 *   return <OfflineIndicator pendingCount={pendingMutationCount} />;
 * }
 * ```
 *
 * Requirements: 6.3, 6.5, 10.2, 10.3
 */
export function useOffline(): UseOfflineResult {
  const [isOnline, setIsOnline] = useState(() => offlineManager.getIsOnline());
  const [pendingMutationCount, setPendingMutationCount] = useState(() =>
    offlineManager.getPendingMutationCount()
  );
  const [isSyncing, setIsSyncing] = useState(false);
  const [justReconnected, setJustReconnected] = useState(false);
  const [lastConflict, setLastConflict] = useState<SyncConflict | null>(null);

  // Subscribe to connectivity changes
  useEffect(() => {
    let wasOffline = !offlineManager.getIsOnline();

    const unsubscribeConnectivity = offlineManager.onConnectivityChange((online) => {
      setIsOnline(online);

      // Track if we just came back online
      if (online && wasOffline) {
        setJustReconnected(true);
        // Auto-clear after 3 seconds
        setTimeout(() => {
          setJustReconnected(false);
        }, 3000);
      }

      wasOffline = !online;
    });

    return unsubscribeConnectivity;
  }, []);

  // Subscribe to pending mutation count changes
  useEffect(() => {
    const unsubscribePending = offlineManager.onPendingCountChange((count) => {
      setPendingMutationCount(count);
    });

    return unsubscribePending;
  }, []);

  // Subscribe to sync completion
  useEffect(() => {
    const unsubscribeSyncComplete = offlineManager.onSyncComplete(() => {
      setIsSyncing(false);
    });

    return unsubscribeSyncComplete;
  }, []);

  // Subscribe to sync conflicts
  useEffect(() => {
    const unsubscribeConflict = offlineManager.onSyncConflict((conflict) => {
      setLastConflict(conflict);
    });

    return unsubscribeConflict;
  }, []);

  const clearReconnectedFlag = useCallback(() => {
    setJustReconnected(false);
  }, []);

  const clearConflict = useCallback(() => {
    setLastConflict(null);
  }, []);

  return {
    isOnline,
    pendingMutationCount,
    isSyncing,
    justReconnected,
    clearReconnectedFlag,
    lastConflict,
    clearConflict,
  };
}

export default useOffline;
